import { GameResult } from '../models/GameResult';
import { GameResultService } from '../services/GameResultService';

type RankingType = 'daily' | 'weekly' | 'allTime';

interface RankingStats {
  totalGames: number;
  uniquePlayers: number;
}

export class InlineRanking {
  private container: HTMLElement;
  private gameResultService: GameResultService;
  private currentType: RankingType = 'daily';
  private isLoading: boolean = false;
  private allResults: Map<RankingType, GameResult[]> = new Map();
  
  constructor() {
    this.gameResultService = new GameResultService();
    this.container = document.createElement('div');
    this.container.className = 'inline-ranking';
    this.render();
    this.loadAllRankings();
  }
  
  private render(): void {
    this.container.innerHTML = `
      <div class="inline-ranking-header">
        <h2>🏆 ランキング</h2>
        <div class="ranking-tabs">
          <button class="ranking-tab ${this.currentType === 'daily' ? 'active' : ''}" data-type="daily">
            本日
          </button>
          <button class="ranking-tab ${this.currentType === 'weekly' ? 'active' : ''}" data-type="weekly">
            今週
          </button>
          <button class="ranking-tab ${this.currentType === 'allTime' ? 'active' : ''}" data-type="allTime">
            全期間
          </button>
        </div>
      </div>
      
      <div class="inline-ranking-content">
        <div class="ranking-stats">
          <div class="stat">
            <span class="stat-label">参加人数:</span>
            <span class="stat-value" id="unique-players">-</span>
          </div>
          <div class="stat">
            <span class="stat-label">プレイ回数:</span>
            <span class="stat-value" id="total-games">-</span>
          </div>
        </div>
        
        <div class="inline-ranking-list">
          <div class="loading">読み込み中...</div>
        </div>
      </div>
    `;
    
    this.attachEventHandlers();
  }
  
  private attachEventHandlers(): void {
    // タブ切り替え
    const tabs = this.container.querySelectorAll('.ranking-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const type = (e.target as HTMLElement).dataset.type as RankingType;
        if (type && type !== this.currentType) {
          this.switchTab(type);
        }
      });
    });
  }
  
  private switchTab(type: RankingType): void {
    this.currentType = type;
    
    // タブのアクティブ状態を更新
    const tabs = this.container.querySelectorAll('.ranking-tab');
    tabs.forEach(tab => {
      if ((tab as HTMLElement).dataset.type === type) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });
    
    // ランキングを表示
    this.displayRanking(type);
  }
  
  private async loadAllRankings(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    
    try {
      // limitなしで全データを取得
      const [dailyResults, weeklyResults, allTimeResults] = await Promise.all([
        this.gameResultService.getDailyRanking(1000),  // 実質無制限
        this.gameResultService.getWeeklyRanking(1000),
        this.gameResultService.getAllTimeRanking(1000),
      ]);
      
      this.allResults.set('daily', dailyResults);
      this.allResults.set('weekly', weeklyResults);
      this.allResults.set('allTime', allTimeResults);
      
      // 現在選択中のタブを表示
      this.displayRanking(this.currentType);
      
    } catch (error) {
      console.error('Error loading rankings:', error);
      const listContainer = this.container.querySelector('.inline-ranking-list');
      if (listContainer) {
        listContainer.innerHTML = '<div class="error">ランキングの読み込みに失敗しました</div>';
      }
    } finally {
      this.isLoading = false;
    }
  }
  
  private displayRanking(type: RankingType): void {
    const rankings = this.allResults.get(type) || [];
    const stats = this.calculateStats(rankings);
    
    // 統計を更新
    const uniquePlayersEl = this.container.querySelector('#unique-players');
    const totalGamesEl = this.container.querySelector('#total-games');
    if (uniquePlayersEl) uniquePlayersEl.textContent = `${stats.uniquePlayers}人`;
    if (totalGamesEl) totalGamesEl.textContent = `${stats.totalGames}回`;
    
    // ランキングリストを更新
    const listContainer = this.container.querySelector('.inline-ranking-list');
    if (!listContainer) return;
    
    if (rankings.length === 0) {
      listContainer.innerHTML = '<div class="no-results">まだ記録がありません</div>';
    } else {
      // 上位10件のみ表示
      const topRankings = rankings.slice(0, 10);
      listContainer.innerHTML = this.renderRankingList(topRankings);
    }
  }
  
  private calculateStats(rankings: GameResult[]): RankingStats {
    const uniqueUsers = new Set(rankings.map(r => r.userId));
    return {
      totalGames: rankings.length,
      uniquePlayers: uniqueUsers.size,
    };
  }
  
  private renderRankingList(rankings: GameResult[]): string {
    return `
      <div class="inline-ranking-items">
        ${rankings.map((result, index) => this.renderRankingItem(result, index + 1)).join('')}
      </div>
    `;
  }
  
  private renderRankingItem(result: GameResult, rank: number): string {
    const medalEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';
    const time = this.formatTime(result.completionTime);
    
    return `
      <div class="inline-ranking-item rank-${rank}">
        <div class="rank-number">
          ${medalEmoji || rank}
        </div>
        <div class="rank-name">
          ${result.userDisplayName}
        </div>
        <div class="rank-time">
          ${time}
        </div>
      </div>
    `;
  }
  
  private formatTime(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const ms = milliseconds % 1000;
    
    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${Math.floor(ms / 100)}`;
    } else {
      return `${seconds}.${Math.floor(ms / 10).toString().padStart(2, '0')}秒`;
    }
  }
  
  public refresh(): void {
    this.loadAllRankings();
  }
  
  public mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }
  
  public unmount(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}